import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { Gift, X, LucideIconData } from 'lucide-angular';

import { IconComponent } from '../icon/icon.component';
import { ButtonComponent } from '../button/button.component';
import { InputComponent } from '../input/input.component';
import { PpCoinComponent } from '../pp-coin/pp-coin.component';
import { PerronPointsModalService } from '../../services/perron-points-modal.service';
import { PointsService } from '../../services/points.service';
import { PerronPointsError } from '../../models/perron-points.model';
import { PERRON_POINTS_COPY, withRedeemSuccess } from '../../perron-points/perron-points-copy';

type RedeemStatus =
  | 'idle'
  | 'pending'
  | 'success'
  | 'invalid'
  | 'consumed'
  | 'throttled'
  | 'error';

@Component({
  selector: 'app-perron-points-modal',
  imports: [IconComponent, ButtonComponent, InputComponent, PpCoinComponent],
  templateUrl: './perron-points-modal.component.html',
  styleUrl: './perron-points-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerronPointsModalComponent {
  private readonly modalService = inject(PerronPointsModalService);
  private readonly pointsService = inject(PointsService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly copy = PERRON_POINTS_COPY.redeemModal;
  protected readonly isOpen = this.modalService.isOpen;

  protected readonly giftIcon: LucideIconData = Gift;
  protected readonly closeIcon: LucideIconData = X;

  protected readonly status = signal<RedeemStatus>('idle');
  protected readonly code = signal('');
  protected readonly successResult = signal<{ points: number; balance: number } | null>(null);

  private readonly closeButtonRef = viewChild<ElementRef<HTMLButtonElement>>('closeBtn');
  private readonly inputWrapperRef = viewChild<ElementRef<HTMLElement>>('inputWrap');
  private readonly modalRef = viewChild<ElementRef<HTMLElement>>('modalEl');
  private previouslyFocusedElement: HTMLElement | null = null;

  /** UX gate: 12 caracteres tras quitar guiones/espacios. La validación real la hace el backend (422). */
  protected readonly canSubmit = computed(() => {
    if (this.status() === 'pending') return false;
    return this.code().replace(/[-\s]/g, '').length === 12;
  });

  constructor() {
    // Reset + focus al abrir; restore focus al cerrar.
    effect(() => {
      const open = this.isOpen();
      if (!isPlatformBrowser(this.platformId)) return;

      if (open) {
        this.resetState();
        this.previouslyFocusedElement = (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => {
          const input = this.inputWrapperRef()?.nativeElement.querySelector('input');
          (input as HTMLInputElement | null)?.focus();
        });
      } else if (this.previouslyFocusedElement) {
        const el = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;
        queueMicrotask(() => {
          if (document.contains(el)) {
            el.focus({ preventScroll: true });
          }
        });
      }
    });

    // ESC cierra.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.key === 'Escape' && this.isOpen()) {
            this.onClose();
          }
        });
    });

    // Focus trap: Tab/Shift+Tab circulan solo dentro del modal.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.key !== 'Tab' || !this.isOpen()) return;

          const modal = this.modalRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) return;

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  protected onClose(): void {
    if (this.status() === 'pending') return;
    this.modalService.close();
  }

  protected onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  protected onSubmit(event?: Event): void {
    event?.preventDefault();
    if (!this.canSubmit()) return;

    this.status.set('pending');
    // Bloquea el auto-close por NavigationEnd del servicio mientras el canje
    // está en vuelo (mismo guard que ya aplica `onClose()` para el cierre manual).
    this.modalService.setRedeeming(true);

    this.pointsService
      .redeem(this.code())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.modalService.setRedeeming(false);
          this.successResult.set(res);
          this.status.set('success');
        },
        error: (err: unknown) => {
          this.modalService.setRedeeming(false);
          if (err instanceof PerronPointsError) {
            if (err.reason === 'invalid') {
              this.status.set('invalid');
              return;
            }
            if (err.reason === 'consumed') {
              this.status.set('consumed');
              return;
            }
            if (err.reason === 'throttled') {
              this.status.set('throttled');
              return;
            }
          }
          this.status.set('error');
        },
      });
  }

  protected successMessage(): string {
    const result = this.successResult();
    if (!result) return '';
    return withRedeemSuccess(this.copy.success, result.points, result.balance);
  }

  private resetState(): void {
    this.status.set('idle');
    this.code.set('');
    this.successResult.set(null);
  }

  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
